import { Box, Grid, Stack, Typography, useTheme } from '@mui/material'
import React, { ReactNode } from 'react'

/**
 * Footer item configuration
 */
export interface ProfileFooterItem {
  /** Label for the item */
  label: string
  /** Value to display */
  value: ReactNode
  /** Custom value color */
  valueColor?: string
  /** Custom value font size */
  valueFontSize?: number | string
  /** Custom label font size */
  labelFontSize?: number | string
}

/**
 * ProfileFooter props
 */
export interface ProfileFooterProps {
  /** Array of items to display */
  items: ProfileFooterItem[]
  /** Custom sx styles */
  sx?: object
  /** Padding */
  padding?: number | string
  /** Show top border */
  showBorder?: boolean
  /** Border color */
  borderColor?: string
  /** Gap between items */
  gap?: number
}

/**
 * ProfileFooter - A footer component for displaying summary/totals information
 *
 * @example
 * ```tsx
 * <ProfileFooter
 *   items={[
 *     { label: 'Gross Profit', value: '$1,234.00', valueColor: 'text.success' },
 *     { label: 'Monthly Total', value: '$5,678.00' },
 *     { label: 'Total', value: '$12,345.00' },
 *   ]}
 * />
 * ```
 */
const ProfileFooter: React.FC<ProfileFooterProps> = ({
  items,
  sx,
  padding = 3,
  showBorder = true,
  borderColor,
  gap = 3,
}) => {
  const theme = useTheme()

  return (
    <Grid
      item
      xs={12}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        p: padding,
        borderTop: showBorder ? '1px solid' : undefined,
        borderColor: showBorder ? borderColor ?? 'text.disabled' : undefined,
        gap,
        ...sx,
      }}
    >
      {items.map((item, index) => (
        <Stack
          key={index}
          sx={{
            width: '100%',
            alignItems: 'flex-start',
            textAlign: 'left',
            paddingLeft: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              fontSize: item.labelFontSize ?? 16,
            }}
          >
            {item.label}
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Typography
              sx={{
                color: item.valueColor ?? theme.palette.text.primary,
                fontWeight: 700,
                fontSize: item.valueFontSize ?? 20,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Grid>
  )
}

export default ProfileFooter
